// src/utils/lebanonTime.js
/* =========================================================
   Class times on the website are Lebanon wall-clock times.
   Lebanon changes its clocks twice a year (UTC+3 in summer, UTC+2 in winter),
   so never add a fixed "+3": these helpers ask the browser's time-zone data.
   ========================================================= */
export const LEBANON_TZ = 'Asia/Beirut';

// minutes a time zone is ahead of UTC at a given moment (handles summer/winter time)
export const tzOffsetMin = (date, tz) => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(date);
    const m = Object.fromEntries(parts.map((x) => [x.type, x.value]));
    const asUTC = Date.UTC(+m.year, +m.month - 1, +m.day, +m.hour, +m.minute, +m.second);
    return Math.round((asUTC - Math.floor(date.getTime() / 1000) * 1000) / 60000);
  } catch {
    return null;
  }
};

// The real moment of a Lebanon wall-clock time (month is 1-12)
export const fromLebanonWall = (y, mo, d, h, mi = 0) => {
  const guess = Date.UTC(y, mo - 1, d, h, mi);
  let t = guess - (tzOffsetMin(new Date(guess), LEBANON_TZ) ?? 180) * 60000;
  t = guess - (tzOffsetMin(new Date(t), LEBANON_TZ) ?? 180) * 60000; // second pass: right side of a clock change
  return new Date(t);
};

// Today's date in Lebanon: { y, mo, d, dow } with dow 1 = Monday … 7 = Sunday
export const lebanonToday = (now = new Date()) => {
  const off = tzOffsetMin(now, LEBANON_TZ) ?? 180;
  const w = new Date(now.getTime() + off * 60000);
  const js = w.getUTCDay();
  return { y: w.getUTCFullYear(), mo: w.getUTCMonth() + 1, d: w.getUTCDate(), dow: js === 0 ? 7 : js };
};

// Next real moment of a weekly Lebanon slot (day 1 = Monday … 7 = Sunday)
export const nextLebanonSlot = (day, h, mi = 0, now = new Date()) => {
  const t = lebanonToday(now);
  const add = (day - t.dow + 7) % 7;
  const base = new Date(Date.UTC(t.y, t.mo - 1, t.d + add));
  return fromLebanonWall(base.getUTCFullYear(), base.getUTCMonth() + 1, base.getUTCDate(), h, mi);
};

// "UTC+3" / "UTC-4:30" for a zone at a moment
export const utcLabel = (tz, date = new Date()) => {
  const m = tzOffsetMin(date, tz);
  if (m === null) return '';
  const sign = m < 0 ? '-' : '+';
  const a = Math.abs(m);
  return `UTC${sign}${Math.floor(a / 60)}${a % 60 ? `:${String(a % 60).padStart(2, '0')}` : ''}`;
};
